import { ToastCustom } from "@components";
import { FileUpload } from "@components/file-upload";
import { FormFieldControl } from "@components/form-field-control";
import { toast } from "sonner";
import { cx } from "@/utils/cx";
import {
	INFRACTION_CREATE_ALLOWED_EXTENSIONS,
	INFRACTION_CREATE_ALLOWED_EXTENSIONS_LABEL,
	INFRACTION_CREATE_MAX_FILE_SIZE_BYTES,
} from "../constants/infraction-create.constant";
import type { InfractionExistingAttachment } from "../handlers/map-infraction-attachments-to-slots";
import { getInfractionAttachmentFileIconType } from "../utils/infraction-attachment.utils";

type InfractionCreateAttachmentFieldProps = {
	label: string;
	htmlFor: string;
	file: File | null;
	onFileChange: (file: File | null) => void;
	existingFile?: InfractionExistingAttachment | null;
	onExistingFileRemove?: () => void;
	allowRemoveExisting?: boolean;
	disabled?: boolean;
	isRequired?: boolean;
	error?: string;
};

function buildExtensionsAccept(): string {
	return INFRACTION_CREATE_ALLOWED_EXTENSIONS.join(",");
}

function buildDropZoneHint(): string {
	const sizeHint = `Tamanho máximo: ${INFRACTION_CREATE_MAX_FILE_SIZE_BYTES / 1024 / 1024} MB`;

	return `Formatos aceitos: ${INFRACTION_CREATE_ALLOWED_EXTENSIONS_LABEL}.\n${sizeHint}`;
}

export function InfractionCreateAttachmentField({
	label,
	htmlFor,
	file,
	onFileChange,
	existingFile = null,
	onExistingFileRemove,
	allowRemoveExisting = false,
	disabled = false,
	isRequired = false,
	error,
}: InfractionCreateAttachmentFieldProps) {
	const hasExistingFile = existingFile !== null && existingFile !== undefined;
	const shouldShowExistingFile = hasExistingFile && !file;
	const existingFileIconType = hasExistingFile
		? getInfractionAttachmentFileIconType(existingFile.fileName)
		: "empty";
	const selectedFileIconType = file
		? getInfractionAttachmentFileIconType(file.name)
		: "empty";

	const handleExistingFileRemove = () => {
		if (!allowRemoveExisting || disabled) {
			return;
		}

		onExistingFileRemove?.();
		onFileChange(null);
	};

	return (
		<FormFieldControl
			error={error}
			htmlFor={htmlFor}
			isRequired={isRequired}
			label={label}
		>
			<FileUpload.Root className="w-full">
				{!(hasExistingFile || file) && (
					<FileUpload.DropZone
						accept={buildExtensionsAccept()}
						allowsMultiple={false}
						className={cx(!!error && "ring-border-error_subtle")}
						hint={buildDropZoneHint()}
						isDisabled={disabled}
						maxSize={INFRACTION_CREATE_MAX_FILE_SIZE_BYTES}
						onDropFiles={(files) => {
							const selected = files.item(0);
							if (selected) {
								onFileChange(selected);
							}
						}}
						onDropUnacceptedFiles={() => {
							toast(
								ToastCustom({
									description:
										"Arquivo não aceito. Verifique o formato ou tamanho.",
									title: "Erro no upload",
									variant: "error",
								})
							);
						}}
						onSizeLimitExceed={() => {
							toast(
								ToastCustom({
									description: `Tamanho máximo excedido. Envie um arquivo de até ${INFRACTION_CREATE_MAX_FILE_SIZE_BYTES / 1024 / 1024}MB.`,
									title: "Arquivo grande demais",
									variant: "error",
								})
							);
						}}
					/>
				)}
				{shouldShowExistingFile ? (
					<FileUpload.List>
						<FileUpload.ListItemProgressBar
							fileIconVariant="solid"
							key={`existing-${existingFile.id}`}
							name={existingFile.fileName}
							onDelete={handleExistingFileRemove}
							progress={100}
							size={existingFile.fileSize ?? 0}
							type={existingFileIconType}
						/>
					</FileUpload.List>
				) : null}
				{!!file && (
					<FileUpload.List>
						<FileUpload.ListItemProgressBar
							fileIconVariant="solid"
							key={file.name}
							name={file.name}
							onDelete={() => {
								onFileChange(null);
							}}
							progress={100}
							size={file.size}
							type={selectedFileIconType}
						/>
					</FileUpload.List>
				)}
			</FileUpload.Root>
		</FormFieldControl>
	);
}
